import { useMemo, useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Input,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tag,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add24Regular, ArrowClockwise24Regular, Folder20Regular, FolderAdd20Regular, Search20Regular } from "@fluentui/react-icons";
import { VALUTAZIONI, type Valutazione } from "@app/schema";
import { useData } from "../data/DataProvider";
import { useServizi } from "../data/useServizi";
import { eAlbo, giorniAllaScadenza, loadGare, oggettoGara, ordinaGare, statoVisuale, testoRicerca, type Gara, type StatoVisuale } from "../data/gare";
import { useAsync } from "../lib/useAsync";
import { formatDateTime, formatEuro } from "../lib/format";
import { navigate, replace, type FiltroRapido, type VistaGare } from "../lib/nav";
import { useWide } from "../lib/useWide";
import { Shell } from "../components/Shell";
import { ChipGroup } from "../components/ChipGroup";
import { GaraCard } from "../components/GaraCard";
import { ScadenzaBadge, ValutazioneBadge } from "../components/Badges";
import { ErrorState } from "../components/ErrorState";
import { Codice } from "../components/Codice";

const useStyles = makeStyles({
  toolbar: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  search: { flexGrow: 1, minWidth: "260px" },
  select: { minWidth: "180px", maxWidth: "260px" },
  count: { color: tokens.colorNeutralForeground3, display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  empty: { textAlign: "center", color: tokens.colorNeutralForeground2, padding: tokens.spacingVerticalXXL },
  tableWrap: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  row: { cursor: "pointer" },
  codice: { display: "block", fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: "tabular-nums", paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS },
  oggetto: { display: "flex", flexDirection: "column", gap: "2px", paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS },
  oggettoTitolo: { fontWeight: tokens.fontWeightSemibold },
  sub: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  scadenza: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" },
  cartellaOk: { display: "inline-flex", alignItems: "center", gap: "4px", color: tokens.colorNeutralForeground2 },
  cartellaDa: { display: "inline-flex", alignItems: "center", gap: "4px", color: tokens.colorPaletteMarigoldForeground1 },
});

const VISTE: Array<{ v: VistaGare; stato: StatoVisuale }> = [
  { v: "attive", stato: "Attiva" },
  { v: "scadute", stato: "Scaduta" },
  { v: "archiviate", stato: "Archiviata" },
];
const ETICHETTE: Record<VistaGare, string> = { attive: "In corso", scadute: "Scadute", archiviate: "Archiviate" };
const FILTRI: Record<FiltroRapido, { label: string; test: (g: Gara) => boolean }> = {
  "da-valutare": { label: "Da valutare", test: (g) => (g.fields.Valutazione ?? "Da valutare") === "Da valutare" },
  "cartelle-da-creare": { label: "Cartelle da creare", test: (g) => g.fields.Valutazione === "Partecipa" && !g.fields.CartellaId },
  "in-scadenza": { label: "Scadono entro 7 giorni", test: (g) => (giorniAllaScadenza(g) ?? Infinity) <= 7 },
};

export function ElencoGare({ vista, filtro }: { vista: VistaGare; filtro?: FiltroRapido }) {
  const s = useStyles();
  const data = useData();
  const wide = useWide();
  const { servizi, byId } = useServizi();
  const [valutazione, setValutazione] = useState<Valutazione>();
  const [servizio, setServizio] = useState<number>();
  const [testo, setTesto] = useState("");
  const archiviate = vista === "archiviate";
  const { data: gare, error, reload } = useAsync(() => loadGare(data, archiviate), [data, archiviate]);
  const stato = VISTE.find((x) => x.v === vista)!.stato;

  const visibili = useMemo(() => {
    if (!gare) return undefined;
    const t = testo.trim().toLowerCase();
    return ordinaGare(
      gare.filter(
        (g) =>
          !eAlbo(g) &&
          statoVisuale(g) === stato &&
          (!filtro || FILTRI[filtro].test(g)) &&
          (!valutazione || (g.fields.Valutazione ?? "Da valutare") === valutazione) &&
          (!servizio || g.fields.Servizio === servizio) &&
          (!t || testoRicerca(g, byId.get(g.fields.Servizio ?? 0)?.fields.Sigla).includes(t)),
      ),
    );
  }, [gare, stato, filtro, valutazione, servizio, testo, byId]);

  const apri = (g: Gara) => navigate(`/g/${g.id}`);

  return (
    <Shell
      title="Gare"
      actions={
        <>
          <Button appearance="subtle" icon={<ArrowClockwise24Regular />} aria-label="Aggiorna" onClick={reload} />
          {data.roles.gestore && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => navigate("/nuova")}>
              Nuova gara
            </Button>
          )}
        </>
      }
    >
      <div className={s.toolbar}>
        <ChipGroup ariaLabel="Stato" options={VISTE.map((x) => x.v)} labels={ETICHETTE} value={vista} onChange={(v) => replace(`/gare/${v}`)} />
        <Input
          className={s.search}
          contentBefore={<Search20Regular />}
          placeholder="Cerca per codice, oggetto, stazione appaltante, CIG…"
          value={testo}
          onChange={(_, d) => setTesto(d.value)}
        />
        <Select
          className={s.select}
          aria-label="Valutazione"
          value={valutazione ?? ""}
          onChange={(_, d) => setValutazione((d.value || undefined) as Valutazione | undefined)}
        >
          <option value="">Tutte le valutazioni</option>
          {VALUTAZIONI.map((v) => (
            <option key={v}>{v}</option>
          ))}
        </Select>
        <Select
          className={s.select}
          aria-label="Servizio"
          value={servizio ? String(servizio) : ""}
          onChange={(_, d) => setServizio(d.value ? Number(d.value) : undefined)}
        >
          <option value="">Tutti i servizi</option>
          {(servizi ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.fields.Sigla} – {x.fields.Title}
            </option>
          ))}
        </Select>
      </div>

      {error ? (
        <ErrorState error={error} />
      ) : !visibili ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : (
        <>
          <Caption1 className={s.count}>
            {visibili.length} {visibili.length === 1 ? "gara" : "gare"}
            {filtro && (
              <Tag size="small" dismissible dismissIcon={{ "aria-label": "Togli il filtro" }} value={filtro} onClick={() => replace(`/gare/${vista}`)}>
                {FILTRI[filtro].label}
              </Tag>
            )}
          </Caption1>
          {visibili.length === 0 ? (
            <Body1 className={s.empty}>Nessuna gara corrisponde ai filtri.</Body1>
          ) : wide ? (
            <div className={s.tableWrap}>
              <Table aria-label="Gare">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: 200 }}>Codice</TableHeaderCell>
                    <TableHeaderCell>Oggetto e stazione appaltante</TableHeaderCell>
                    <TableHeaderCell style={{ width: 130 }}>Importo</TableHeaderCell>
                    <TableHeaderCell style={{ width: 180 }}>Scadenza</TableHeaderCell>
                    <TableHeaderCell style={{ width: 130 }}>Valutazione</TableHeaderCell>
                    <TableHeaderCell style={{ width: 150 }}>Cartelle</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibili.map((g) => (
                    <TableRow
                      key={g.id}
                      className={s.row}
                      tabIndex={0}
                      onClick={() => apri(g)}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && apri(g)}
                    >
                      <TableCell>
                        <span className={s.codice}>
                          <Codice value={g.fields.Codice} />
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className={s.oggetto}>
                          <span className={s.oggettoTitolo}>{oggettoGara(g)}</span>
                          <span className={s.sub}>
                            {g.fields.Stazione}
                            {g.fields.PerLotti ? " · a lotti" : ""}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{g.fields.Importo != null ? formatEuro(g.fields.Importo) : ""}</TableCell>
                      <TableCell>
                        <div className={s.scadenza}>
                          <span>{g.fields.Scadenza ? formatDateTime(g.fields.Scadenza) : "—"}</span>
                          {stato === "Attiva" && <ScadenzaBadge g={g} />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ValutazioneBadge g={g} />
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          {g.fields.CartellaId ? (
                            <span className={s.cartellaOk}>
                              <Folder20Regular /> Create
                            </span>
                          ) : g.fields.Valutazione === "Partecipa" ? (
                            <span className={s.cartellaDa}>
                              <FolderAdd20Regular /> {g.fields.PosizioneId ? "Da completare" : "Da creare"}
                            </span>
                          ) : null}
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            visibili.map((g) => <GaraCard key={g.id} g={g} servizio={byId.get(g.fields.Servizio ?? 0)} onOpen={() => apri(g)} />)
          )}
        </>
      )}
    </Shell>
  );
}
